/** Settings that determine who can see site members' profiles and who is allowed to make member profiles public. */
export interface MemberPrivacySettings {
  /**
   * Member privacy settings ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /** Who can see the public member profiles. */
  publicMemberVisibility?: VisibilityWithLiterals;
  /** Who can make the member profile public. */
  publicMemberCandidates?: CandidatesWithLiterals;
  /** Revision number, which increments by 1 each time the member privacy settings are updated. To prevent conflicting changes, the existing revision must be used when updating member privacy settings. */
  revision?: string | null;
  /**
   * Date and time when the member privacy settings were updated.
   * @readonly
   */
  updatedDate?: Date | null;
}

export enum Visibility {
  /** Unknown member visibility. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** The members' profiles are only visible to logged-in site members. */
  VISIBLE_TO_MEMBERS_ONLY = 'VISIBLE_TO_MEMBERS_ONLY',
  /** All site visitors can see the members' profiles. */
  VISIBLE_TO_ALL = 'VISIBLE_TO_ALL',
}

/** @enumType */
export type VisibilityWithLiterals =
  | Visibility
  | 'UNKNOWN'
  | 'VISIBLE_TO_MEMBERS_ONLY'
  | 'VISIBLE_TO_ALL';

export enum Candidates {
  /** Unknown public member candidate. This value isn't used. */
  UNKNOWN = 'UNKNOWN',
  /** All members have private profiles with no option to make them public. */
  NO_ONE = 'NO_ONE',
  /** All members can make the profile public. */
  ANYONE = 'ANYONE',
}

/** @enumType */
export type CandidatesWithLiterals =
  | Candidates
  | 'UNKNOWN'
  | 'NO_ONE'
  | 'ANYONE';

export interface GetMemberPrivacySettingsRequest {}

export interface GetMemberPrivacySettingsResponse {
  /** Retrieved member privacy settings. */
  memberPrivacySettings?: MemberPrivacySettings;
}

export interface BulkGetMemberPrivacySettingsRequest {
  /**
   * List of tenants to retrieve privacy settings for.
   * @minSize 1
   * @maxSize 100
   */
  siteMemberInstanceIds?: string[];
}

export interface BulkGetMemberPrivacySettingsResponse {
  /** List of member privacy settings per site member instance ID. */
  siteMemberInstanceMemberPrivacySettings?: SiteMemberInstanceMemberPrivacySettings[];
}

export interface SiteMemberInstanceMemberPrivacySettings {
  /** The site member instance ID. */
  siteMemberInstanceId?: string;
  /** The member privacy settings for the site member instance. */
  memberPrivacySettings?: MemberPrivacySettings;
}

export interface SetMemberPrivacySettingsRequest {
  /** Member privacy settings details. */
  memberPrivacySettings: MemberPrivacySettings;
}

export interface SetMemberPrivacySettingsResponse {
  /** Member privacy settings. */
  memberPrivacySettings?: MemberPrivacySettings;
}

export interface InvalidRevisionPayload {
  /** Provided revision number. */
  revision?: string | null;
}

export interface InvalidSettingsPayload {
  /** Provided public member visibility value. */
  publicMemberVisibility?: VisibilityWithLiterals;
  /** Provided public member candidates value. */
  publicMemberCandidates?: CandidatesWithLiterals;
}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /**
   * AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default.
   * Not the dev-center dev site (see Flag.DEV_CENTER_DEV_SITE_FLAG).
   */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
  /**
   * Symphony — a siteless site representing a project within Symphony (the evolution of Orion).
   * Holds project data, conversations, assets, and manages collaborators / shared team members for the project.
   */
  SYMPHONY = 'SYMPHONY',
  /** Nautilus platform app. */
  NAUTILUS_APPS = 'NAUTILUS_APPS',
  /**
   * Vibu — a siteless site representing a project built with Vibu (the AI app builder).
   * Holds the project's generated code, conversations, assets, and manages collaborators for the project.
   */
  VIBU = 'VIBU',
  /**
   * Ghost site for the BrandKit multi-site platform. Each ghost backs a single BrandKit
   * (an Enterprise account may have several), holding that BrandKit's brand definition
   * (theme, typography, assets) which is rolled out to the BrandKit's connected sites.
   * MetaSites with this namespace will *not* be shown in a user's site list by default.
   */
  ENTERPRISE_BRAND_KIT = 'ENTERPRISE_BRAND_KIT',
  /**
   * Dev-center development sites (com.wixpress.devcenter.dsm).
   * DEPRECATED: replaced by Flag.DEV_CENTER_DEV_SITE_FLAG (a dev site is orthogonal to the namespace). Do not use for new sites.
   */
  DEV_CENTER_DEV_SITE = 'DEV_CENTER_DEV_SITE',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS'
  | 'SYMPHONY'
  | 'NAUTILUS_APPS'
  | 'VIBU'
  | 'ENTERPRISE_BRAND_KIT'
  | 'DEV_CENTER_DEV_SITE';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type SetMemberPrivacySettingsApplicationErrors =
  | {
      code?: 'INVALID_REVISION';
      description?: string;
      data?: InvalidRevisionPayload;
    }
  | {
      code?: 'INVALID_SETTINGS';
      description?: string;
      data?: InvalidSettingsPayload;
    };
